import { Fragment, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  styled,
  Toolbar,
  Typography,
} from '@mui/material';
import { ReactComponent as LogoIcon } from 'src/assets/icons/logo-icon.svg';
import { ReactComponent as LogoutIcon } from 'src/assets/icons/logout-icon.svg';
import { HEADER_HEIGHT, USER_TYPE_CD } from 'src/assets/static/constants';
import { useStores } from 'src/models';
import { PATH_PAGE } from 'src/routes/paths';

type TNavigationChild = {
  segment: string;
  title: string;
  adminOnly?: boolean;
};

type TNavigation = {
  segment: string;
  title: string;
  adminOnly?: boolean;
  children?: TNavigationChild[];
}[];

export default function DashboardHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname.substring(1); // 맨 앞 / 제거
  const { authStore } = useStores();
  const { t } = useTranslation();

  const isAdmin = authStore.user?.userTypeCd?.code === USER_TYPE_CD.ADMIN;

  const NAVIGATION: TNavigation = [
    {
      segment: 'patient-management',
      title: t('patientManagement.title'),
    },
    {
      segment: 'service-management',
      title: t('serviceManagement.title'),
      adminOnly: true,
      children: [
        {
          segment: 'scenario-management',
          title: t('scenarioManagement.title'),
        },
        // {
        //   segment: 'service-control',
        //   title: t('serviceControl.title'),
        // },
        // {
        //   segment: 'terms-management',
        //   title: t('termsManagement.title'),
        // },
        {
          segment: 'hospital-management',
          title: t('hospitalManagement.title'),
        },
      ],
    },
    {
      segment: 'my-page',
      title: t('myPage.title'),
      children: [
        {
          segment: 'profile-management',
          title: t('myPage.profileManagement.title'),
        },
        {
          segment: 'change-password',
          title: t('myPage.changePassword.title'),
        },
        {
          segment: 'change-language',
          title: t('myPage.changeLanguage.title'),
          adminOnly: true,
        },
        {
          segment: 'member-withdrawal',
          title: t('myPage.memberWithdrawal.title'),
        },
      ],
    },
  ];

  const filteredNavigation = NAVIGATION.filter(
    (item) => !item.adminOnly || (item.adminOnly && isAdmin),
  ).map((item) => ({
    ...item,
    children: item.children?.filter((child) => !child.adminOnly || isAdmin),
  }));

  const [menuState, setMenuState] = useState<{
    anchorEl: HTMLElement | null;
    currentMenu: string;
  }>({
    anchorEl: null,
    currentMenu: '',
  });
  const open = Boolean(menuState.anchorEl);

  const handleMainMenuClick = (
    segment: string,
    event: MouseEvent<HTMLButtonElement>,
    hasChildren: boolean,
  ) => {
    if (hasChildren) {
      setMenuState({
        anchorEl: event.currentTarget,
        currentMenu: segment,
      });
    } else {
      navigate(`/${segment}`);
    }
  };

  const handleMenuClose = () => {
    setMenuState({
      anchorEl: null,
      currentMenu: '',
    });
  };

  const handleSubMenuClick = (segment: string) => {
    handleMenuClose();
    navigate(`/${segment}`);
  };

  const isMainMenuActive = (segment: string) => {
    if (currentPath.startsWith(segment)) {
      return true;
    }

    const menuItem = NAVIGATION.find((item) => item.segment === segment);
    if (menuItem?.children) {
      return menuItem.children.some((child) => currentPath.startsWith(child.segment));
    }

    return false;
  };

  const isSubMenuActive = (segment: string) => {
    return currentPath.startsWith(segment);
  };

  const handleLogout = async () => {
    const result = await authStore.logout();
    if (result?.resultCode === 'S') {
      navigate(PATH_PAGE.home);
    }
  };

  return (
    <MainContainer position="fixed">
      <ToolbarContainer>
        <IconButton onClick={() => navigate(PATH_PAGE.home)}>
          <Logo />
        </IconButton>

        <MainMenuContainer>
          {filteredNavigation.map((item) => (
            <Fragment key={item.segment}>
              {item.children ? (
                <div>
                  <MainMenu
                    isActive={isMainMenuActive(item.segment)}
                    onClick={(e) => handleMainMenuClick(item.segment, e, true)}
                  >
                    {item.title}
                  </MainMenu>

                  <SubMenuContainer
                    anchorEl={menuState.anchorEl}
                    open={open && menuState.currentMenu === item.segment}
                    onClose={handleMenuClose}
                  >
                    {item.children.map((child) => (
                      <SubMenu
                        key={child.segment}
                        isActive={isSubMenuActive(child.segment)}
                        onClick={() => handleSubMenuClick(child.segment)}
                      >
                        {child.title}
                      </SubMenu>
                    ))}
                  </SubMenuContainer>
                </div>
              ) : (
                <MainMenu
                  isActive={isMainMenuActive(item.segment)}
                  onClick={(e) => handleMainMenuClick(item.segment, e, false)}
                >
                  {item.title}
                </MainMenu>
              )}
            </Fragment>
          ))}
        </MainMenuContainer>

        <UserContainer>
          <UserInfo>
            <Name>{authStore.user?.userNm}</Name>
            {authStore.user?.hsptlInfo && (
              <Hospital>
                {authStore.user?.hsptlInfo?.hsptlNm} {t('affiliation')}
              </Hospital>
            )}
          </UserInfo>

          <SeparatorLine orientation="vertical" />

          <HelperContainer>
            <IconButton onClick={handleLogout}>
              <LogoutIcon />
            </IconButton>
          </HelperContainer>
        </UserContainer>
      </ToolbarContainer>
    </MainContainer>
  );
}

const MainContainer = styled(AppBar)(({ theme }) => ({
  padding: '0px 40px',
  gap: '42px',
  backgroundColor: theme.palette.common.white,
  height: `${HEADER_HEIGHT}px`,
  boxShadow: 'none',
  [theme.breakpoints.down('md')]: {
    padding: '0px 12px',
  },
}));

const ToolbarContainer = styled(Toolbar)(() => ({
  padding: '0px !important',
  justifyContent: 'space-between',
}));

const Logo = styled(LogoIcon)(({ theme }) => ({
  width: '50px',
  height: '50px',
  [theme.breakpoints.down('md')]: {
    width: '40px',
    height: '40px',
  },
}));

const MainMenuContainer = styled(Stack)(({ theme }) => ({
  gap: '60px',
  flexDirection: 'row',
  [theme.breakpoints.down('md')]: {
    gap: '16px',
  },
}));

const MainMenu = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ isActive = false, theme }) => ({
  padding: 0,
  fontSize: '18px',
  fontWeight: isActive ? 700 : 400,
  color: isActive ? theme.palette.primary.main : theme.palette.grey[500],
  [theme.breakpoints.down('md')]: {
    fontSize: '15px',
    minWidth: 0,
    whiteSpace: 'nowrap',
  },
}));

const UserContainer = styled(Stack)(({ theme }) => ({
  gap: '30px',
  flexDirection: 'row',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    gap: '4px',
  },
}));

const UserInfo = styled(Stack)(({ theme }) => ({
  gap: '5px',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const Name = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  lineHeight: '150%',
  color: theme.palette.common.black,
}));

const Hospital = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  lineHeight: '150%',
  color: theme.palette.grey[500],
}));

const SeparatorLine = styled(Divider)(({ theme }) => ({
  height: '40px',
  border: `1px solid ${theme.palette.grey[300]}`,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const HelperContainer = styled(Stack)(() => ({
  gap: '20px',
  flexDirection: 'row',
}));

const SubMenuContainer = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '10px',
    border: `1px solid ${theme.palette.grey[300]}`,
    padding: '10px',
    gap: '4px',
    boxShadow: '0px 6px 10px 0px #0000001F',
  },
}));

const SubMenu = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ isActive = false, theme }) => ({
  padding: '6px 10px',
  fontWeight: 500,
  color: isActive ? theme.palette.common.black : theme.palette.grey[500],
  bgcolor: isActive ? theme.palette.grey[100] : null,
}));
